import {
	ProjectDescription,
	StartNewProjectButton,
	ImportProjectButton,
} from "./SelectProjectUtils";
import { useAppContext } from "@contexts/AppProvider";
import { useSelectProject } from "./hooks/useSelectProject";
import { Dispatch, SetStateAction, useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BiFilter, BiFilterAlt, BiTrash } from "react-icons/bi";
import { Form, Formik } from "formik";
import { FieldType } from "@typedefs/modulesInterfaces";
import { selectProjectsFilters, setShowFinalized, setTags, setShowTrashbin } from "@features/project/projectSlice";
import { useAppDispatch, useAppSelector } from "@app/hooks";

import Button from "@components/button/Button";
import ModuleButton from "@components/button/ModuleButton";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import FadeAnimation from "@components/animations/FadeAnimation";
import SearchByTag from "./components/SearchByTag";
import DownloadOfflineApp from "./DownloadOfflineApp";
import ProjectContent from "./components/ProjectContent";
import ProjectModals from "./components/ProjectModals";
import TranslatableText from "@components/translations/TranslatableText";
import LoadMore from "./components/LoadMore";
import ImageCredit from "@components/imageCredit/ImageCredit";

const ProjectBanner = ({ bucketUrl }: { bucketUrl: string }) => (
	<div className="project-banner-container">
		<img
			src={`${bucketUrl}/project_select_banner.jpg`}
			alt="Kakuma, Kenya - Martha Kasafi, a refugee from Democratic Republic of Congo, works at her vegetable crops"
			className="w-100"
			style={{ height: "100%", objectFit: "cover", objectPosition: "top" }}
		/>
		<ImageCredit credit="FAO/Luis Tato/UNJP/KEN/086/EC" />
	</div>
);

interface ProjectHeaderProps {
	isFilterOpenState: [boolean, Dispatch<SetStateAction<boolean>>];
	userName: string;
}

interface Filters {
	showFinalized: boolean;
	tags: number[];
}

const ProjectHeader = ({ isFilterOpenState, userName }: ProjectHeaderProps) => {
	const [isFilterOpen, setIsFilterOpen] = isFilterOpenState;
	const { showFinalized, tags, showTrashbin } = useAppSelector(selectProjectsFilters);
	const dispatch = useAppDispatch();

	const handleSubmit = (values: Filters) => {
		dispatch(setShowFinalized(values.showFinalized));
		dispatch(setTags(values.tags));
	}

	const handleTrashbinClick = () => {
		dispatch(setShowTrashbin(!showTrashbin));
		dispatch(setShowFinalized(false));
		dispatch(setTags([]));
		setIsFilterOpen(false);
	}

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [iconSize, setIconSize] = useState(20);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		setIconSize(windowWidth > 2560 ? 40 : windowWidth > 1920 ? 30 : 20);
	}, [windowWidth]);

	return (
		<div className="d-flex justify-content-between">
			<div className="d-flex f-column">
				<div className="d-flex gap-8">
					<ProjectDescription user={userName} />
				</div>

				<FadeAnimation isShown={isFilterOpen} duration={0.3} ease="easeInOut">
					<div>
						<Formik initialValues={{ showFinalized, tags }} onSubmit={handleSubmit}>
							<Form className="d-flex f-column gap-8 pb-2">
								<SearchByTag />
								<GenericInputGroup inputGroup={{
									type: FieldType.BOOLEAN,
									label: "main.show_finalized",
									inputName: "showFinalized",
								}} />
								<ModuleButton icon={BiFilter} styleType="secondary" labelKey="main.apply_filters" buttonType="submit" />
							</Form>
						</Formik>
					</div>
				</FadeAnimation>

				<FadeAnimation isShown={showTrashbin} duration={0.3} ease="easeInOut">
					<div className="d-flex f-column pb-2">
						<TranslatableText translationKey="main.trashbin" className="ff-medium" />
						<TranslatableText translationKey="main.trashbin_description" className="fs-12 ff-light" style={{ marginTop: "0.1rem" }} />
					</div>
				</FadeAnimation>
			</div>

			<div className="d-flex f-column gap-8">
				<Link
					to="/help"
					className="fs-13 text-quat-de text-end align-self-end underline select-project-help-link"
				>
					<TranslatableText translationKey="help_center.help_center_link" />
				</Link>
				<div className="d-flex pb-2 w-100 justify-content-end gap-8" style={{ alignSelf: "baseline" }}>
					<Button
						className="br-8 bg-tert-br"
						onClick={() => setIsFilterOpen(prev => !prev)}
						disabled={showTrashbin}
						style={{
							padding: "0.4rem 0.6rem",
							backgroundColor: isFilterOpen ? "var(--tertiary-deep)" : "var(--tertiary-bright)",
							border: "1px solid",
							borderColor: isFilterOpen ? "var(--gray)" : "transparent",
							opacity: showTrashbin ? "0.6" : "1"
						}}
					>
						<BiFilterAlt size={iconSize} color="var(--darker-gray)" />
					</Button>
					<Button
						className="br-8 bg-tert-br"
						onClick={handleTrashbinClick}
						style={{
							padding: "0.4rem 0.6rem",
							backgroundColor: showTrashbin ? "var(--error)" : "var(--tertiary-bright)",
							border: "1px solid",
							borderColor: showTrashbin ? "var(--darker-gray)" : "transparent"
						}}
					>
						<BiTrash size={iconSize} color={showTrashbin ? "var(--white)" : "var(--darker-gray)"} />
					</Button>
				</div>
			</div>
		</div>
	);
};

const SelectProject = () => {
	const bucketUrl = useAppContext();
	const { showFinalized, tags } = useAppSelector(selectProjectsFilters);
	const initialIsOpen = showFinalized || tags?.length > 0;
	const isFilterOpenState = useState(initialIsOpen);

	const {
		user,
		tableData,
		columns,
		isError,
		isLoading,
		projectsCount,
		handleNewProject,
		handleNext,
		delete: deleteState,
		remove,
		finalize,
		tags: tagsState,
		copy,
		export: exportState,
		import: importState,
	} = useSelectProject();

	const isLoadMoreVisible = useMemo(() => tableData.length ? projectsCount > tableData.length : false, [tableData, projectsCount]);

	return (
		<div className="login-container" style={{ cursor: copy.isLoadingCopy || importState.isLoadingImport ? 'wait' : 'default' }}>
			<article className="bg-gray">
				<ProjectBanner bucketUrl={bucketUrl} />
				<div className="d-flex f-column pt-1 pb-2 px-2">
					<ProjectHeader isFilterOpenState={isFilterOpenState} userName={user?.first_name ?? ""} />

					<ProjectContent
						isLoading={isLoading}
						isLoadingDelete={deleteState.isLoadingDelete}
						tableData={tableData}
						columns={columns}
						isError={isError}
					/>

					<LoadMore
						isLoading={isLoading}
						isVisible={isLoadMoreVisible}
						loadMore={handleNext}
					/>

					<div className="pt-2 d-flex gap-8">
						<StartNewProjectButton handleClick={handleNewProject} />
						<ImportProjectButton
							handleClick={importState.handleImportClick}
							isLoading={importState.isLoadingImport}
						/>
					</div>
				</div>

				<ProjectModals
					tagsState={tagsState}
					deleteState={deleteState}
					remove={remove}
					finalize={finalize}
					copy={copy}
					exportState={exportState}
					importState={importState}
				/>

				<DownloadOfflineApp />
			</article>
		</div>
	);
};

export default SelectProject;
